import React from "react";
import { $applyNodeReplacement, DecoratorNode, LexicalNode, NodeKey, SerializedLexicalNode, Spread } from "lexical";

import { TruncatedText } from "../truncatedText/TruncatedText";
import { DirectionalHint } from "@fluentui/react";
import { PILL_BASE_STYLE } from "../TrackedChangeOperationTypePill";

export type SerializedTagNode = Spread<
  {
    tagId: string;
    tagLabel: string;
    isDeleted: boolean;
    hasObjects: boolean;
  },
  SerializedLexicalNode
>;

export class TagNode extends DecoratorNode<React.ReactElement> {
  __tagId: string;
  __tagLabel: string;
  __hasObjects: boolean;
  __isDeleted: boolean;

  static getType() {
    return "tag";
  }

  static clone(node: TagNode) {
    return new TagNode(node.__tagId, node.__tagLabel, node.__hasObjects, node.__isDeleted, node.__key);
  }

  constructor(tagId: string, tagLabel: string, hasObjects: boolean, isDeleted: boolean, key?: NodeKey) {
    super(key);
    this.__tagId = tagId;
    this.__tagLabel = tagLabel;
    this.__isDeleted = isDeleted;
    this.__hasObjects = hasObjects;
  }

  createDOM(): HTMLElement {
    const span = document.createElement("span");
    span.setAttribute("data-lexical-tag-id", this.__tagId);

    const bg = !this.__isDeleted && this.__hasObjects ? "var(--color-blue)" : "var(--color-red)";

    Object.assign(span.style, {
      ...PILL_BASE_STYLE,
      backgroundColor: bg,
    });

    span.contentEditable = "false";
    return span;
  }

  updateDOM(prevNode: TagNode, dom: HTMLElement): false {
    if (prevNode.__hasObjects !== this.__hasObjects || prevNode.__isDeleted !== this.__isDeleted) {
      dom.style.backgroundColor = !this.__isDeleted && this.__hasObjects ? "var(--color-blue)" : "var(--color-red)";
    }
    return false;
  }

  setTagData(tagLabel: string, hasObjects: boolean): void {
    const writable = this.getWritable();
    writable.__tagLabel = tagLabel;
    writable.__hasObjects = hasObjects;
  }

  decorate(): React.ReactElement {
    const invalid = this.__isDeleted || !this.__hasObjects;

    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, maxWidth: 220 }}>
        <TruncatedText text={this.__tagLabel} tooltipDirectionalHint={DirectionalHint.bottomCenter} />
        {invalid && (
          <img src="/assets/warningRhombus.svg" alt="warning" style={{ width: 10, height: 10, marginLeft: 5 }} />
        )}
      </span>
    );
  }

  exportJSON(): SerializedTagNode {
    return {
      type: "tag",
      version: 1,
      tagId: this.__tagId,
      tagLabel: this.__tagLabel,
      isDeleted: this.__isDeleted,
      hasObjects: this.__hasObjects,
    };
  }

  static importJSON(json: SerializedTagNode) {
    return $createTagNode(json.tagId, json.tagLabel, json.hasObjects, json.isDeleted);
  }

  isInline() {
    return true;
  }

  getTextContent() {
    return `{{${this.__tagId}}}`;
  }

  isKeyboardSelectable() {
    return false;
  }
}

export function $createTagNode(tagId: string, tagLabel: string, hasObjects: boolean, isDeleted: boolean): TagNode {
  return $applyNodeReplacement(new TagNode(tagId, tagLabel, hasObjects, isDeleted));
}

export function $isTagNode(node: LexicalNode | null): node is TagNode {
  return node instanceof TagNode;
}
